import type { AuditFinding, AuditFindingSeverity } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import { OtsBadge, type OtsBadgeTone } from "../components/OtsBadge";
import { OtsInlineAlert } from "../components/OtsInlineAlert";
import { OtsTooltip } from "../components/OtsTooltip";
import { auditDisplayName } from "./audit-utils";

export type AuditFindingsProps = {
  findings?: AuditFinding[];
  isLoading?: boolean;
  error?: ReactNode;
  /** An action shown under the findings, e.g. a button that audits the ticket again. */
  footer?: ReactNode;
};

const severityTones: Record<AuditFindingSeverity, OtsBadgeTone> = {
  CRITICAL: "danger",
  ERROR: "danger",
  WARNING: "warning",
  INFO: "info",
};
const severityOrder: AuditFindingSeverity[] = ["CRITICAL", "ERROR", "WARNING", "INFO"];

/** Blocking findings first, then by how serious they are. */
export const sortAuditFindings = (findings: AuditFinding[]): AuditFinding[] => [...findings].sort((a, b) =>
  Number(b.blocking) - Number(a.blocking) || severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity));

/** What an audit found on a ticket: each rule that fired, how serious it is, and whether it blocks the ticket. */
export const AuditFindings = ({ findings, isLoading = false, error, footer }: AuditFindingsProps) => {
  if (error != null) return <OtsInlineAlert tone="danger" size="compact" title="The audit findings could not be loaded">{error}</OtsInlineAlert>;
  if (isLoading && !findings) return <p role="status" className="text-sm text-[var(--ots-text-muted)]">Loading findings…</p>;

  return <>
    {findings?.length
      ? <ul aria-label="Audit findings" className="space-y-1.5">
        {sortAuditFindings(findings).map((finding) => (
          <li
            key={finding.id}
            data-blocking={finding.blocking || undefined}
            className={[
              "rounded-md border px-2.5 py-2 text-sm",
              finding.blocking
                ? "border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)]/40"
                : "border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]",
            ].join(" ")}>
            <div className="flex items-start gap-2">
              <span className="min-w-0 flex-1 font-semibold">{finding.title || finding.message || "Finding"}</span>
              {finding.blocking && <OtsBadge tone="danger" size="compact">Blocking</OtsBadge>}
              <OtsBadge tone={severityTones[finding.severity] ?? "neutral"} size="compact">{finding.severity.toLowerCase()}</OtsBadge>
            </div>
            {finding.message && finding.title && <p className="mt-1 text-xs leading-5">{finding.message}</p>}
            <OtsTooltip density="compact" className="mt-1 min-w-0 max-w-full" content={finding.ruleKey}>
              <p tabIndex={0} className="truncate text-[11px] text-[var(--ots-text-muted)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]">
                {[auditDisplayName(finding.ruleKey), finding.blocking && "blocking", finding.ruleVersion != null && `v${finding.ruleVersion}`].filter(Boolean).join(" · ")}
              </p>
            </OtsTooltip>
          </li>
        ))}
      </ul>
      : <p className="text-sm text-[var(--ots-text-muted)]">The latest audit found nothing on this ticket.</p>}
    {footer != null && <div className="mt-2">{footer}</div>}
  </>;
};
